import React, { useState, useEffect } from 'react';
import { Form, Input, DatePicker, Button, Select, Upload, notification } from 'antd';
import Title from 'antd/es/typography/Title';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { uploadImage, fetchImages } from '../apis/uploadApi';
import { createHighlightSection } from '../apis/highLightSectionsApi';

dayjs.extend(utc);

const { RangePicker } = DatePicker;

export default function HighlightSectionForm() {
  const [form] = Form.useForm();
  const [availableImages, setAvailableImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const imgRes = await fetchImages('highlightSections');
        if (imgRes.success) setAvailableImages(imgRes.images || []);
      } catch (e) {
        console.error('Error loading images', e);
      }
    };
    load();
  }, []);

  const normalizeImageValue = (imageValue) => {
    if (!imageValue) return '';

    let value = typeof imageValue === 'string'
      ? imageValue
      : imageValue.url || imageValue.path || imageValue.filename || '';

    if (!value) return '';
    
    const imgIndex = value.indexOf('img/');
    if (imgIndex !== -1) {
      value = value.substring(imgIndex + 4);
    }
    if (value.startsWith('highlightSections/')) {
      value = value.substring(18);
    }

    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:') || value.startsWith('blob:')) {
      try {
        const parsedUrl = new URL(value);
        return parsedUrl.pathname.replace(/^\/+/, '');
      } catch (e) {
        return value;
      }
    }
    return value.replace(/^\/+/, '');
  };

  const resolveImageSrc = (imageValue) => {
    const normalizedValue = normalizeImageValue(imageValue);
    if (!normalizedValue) return '';
    if (normalizedValue.startsWith('http://') || normalizedValue.startsWith('https://') || normalizedValue.startsWith('data:') || normalizedValue.startsWith('blob:')) {
      return normalizedValue;
    }
    if (normalizedValue.startsWith('img/')) return `/${normalizedValue}`;
    return `/img/highlightSections/${normalizedValue}`;
  };

  const handleBeforeUpload = (file) => {
    setSelectedFile(file);
    const tempImg = { url: URL.createObjectURL(file), name: `[Nueva] ${file.name}`, isTemp: true };
    setAvailableImages(prev => {
      const filtered = prev.filter(img => !img.isTemp);
      return [...filtered, tempImg];
    });
    form.setFieldsValue({ asset: file.name });
    return false; // Prevent automatic upload
  };

  const handleClearForm = () => {
    form.resetFields();
    setSelectedFile(null);
  };

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      let finalAsset = values.asset;
      
      if (selectedFile) {
        setUploadingImage(true);
        const res = await uploadImage(selectedFile, 'highlightSections');
        if (res.success) {
          finalAsset = normalizeImageValue(res.url);
        } else {
          throw new Error('Error al subir la imagen');
        }
      } else {
          finalAsset = normalizeImageValue(finalAsset);
      }

      const payload = {
        type: 'image',
        asset: finalAsset,
        link: values.link || null,
        start_date: values.dates && values.dates[0] ? values.dates[0].format('YYYY-MM-DD') : null,
        end_date: values.dates && values.dates[1] ? values.dates[1].format('YYYY-MM-DD') : null,
      };

      const res = await createHighlightSection(payload);
      if (res && res.data) {
        notification.success({
          message: 'Sección creada',
          description: 'El highlight section ha sido guardado exitosamente.',
          placement: 'topRight',
        });
        handleClearForm();
      } else {
         throw new Error(res.error || 'Error al guardar');
      }

    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error',
        description: err.response?.data?.error || err.message,
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const wrapperClass = "input-group col-10 col-md-5 p-0 mx-auto mt-3";

  return (
    <div className="container mt-4">
      <Form
        form={form}
        name="highlightSectionForm"
        className="mobile-block themed-block p-3 bg-white shadow-sm rounded"
        layout="vertical"
        onFinish={handleFinish}
        autoComplete="off"
      >
        <Title level={2} className="text-center">Destacar Imagen</Title>

        <div className={wrapperClass}>
          <label className="form-label">Imagen *:</label>
          <div className="w-100 d-flex gap-2">
            <Form.Item name="asset" rules={[{ required: true, message: 'Selecciona una imagen' }]} className="w-100 mb-0">
              <Select
                className="w-100"
                placeholder="Selecciona una imagen existente"
                options={availableImages.map(img => ({
                  value: normalizeImageValue(img.url),
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={resolveImageSrc(img.url)} alt={img.name} style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4 }} />
                      <span>{img.name}</span>
                    </div>
                  ),
                  name: img.name
                }))}
                onChange={(value) => {
                  if (value !== selectedFile?.name) {
                    setSelectedFile(null);
                  }
                }}
                filterOption={(input, option) =>
                  (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                showSearch
              />
            </Form.Item>
            <Upload beforeUpload={handleBeforeUpload} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploadingImage}>Subir</Button>
            </Upload>
          </div>
        </div>

        <div className={wrapperClass}>
          <label className="form-label">Enlace URL (Opcional):</label>
          <Form.Item name="link" className="w-100 mb-0">
            <Input className='m-0' placeholder="https://ejemplo.com" type="url" />
          </Form.Item>
        </div>

        <div className={wrapperClass}>
          <label className="form-label">Rango de fechas de visualización *:</label>
          <Form.Item name="dates" rules={[{ required: true, message: 'Selecciona el rango de fechas' }]} className="w-100 mb-0">
            <RangePicker className="w-100" />
          </Form.Item>
        </div>

        <div className={"input-group col-10 col-md-5 p-0 mx-auto mt-4 d-flex justify-content-center gap-3"}>
          <Button onClick={handleClearForm}>
            Limpiar Formulario
          </Button>
          <Button type="primary" className='themed-btn' htmlType="submit" loading={loading || uploadingImage}>
            Crear
          </Button>
        </div>
      </Form>
    </div>
  );
}
